import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from '@app/core/services/API/api-service';
import { UserSettings, MailTemplate } from '@app/shared/components/my-settings/my-settings';

export interface NavigationData {
  name: string;
  path: string;
}

export interface NavigationDetails {
  _id: string;
  navigationData: NavigationData[];
  createdAt: string;
  status: string;
}

export interface UserSettingsResponse {
  success: boolean;
  data: {
    userDetails: any;
    navigationDetails: NavigationDetails[];
    mailTemplates?: MailTemplate[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class UserSettingsService {
  private settingsSubject = new BehaviorSubject<UserSettings | null>(null);
  private navigationOptionsSubject = new BehaviorSubject<Array<{ value: string; label: string }>>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private isLoadedSubject = new BehaviorSubject<boolean>(false);
  private loadedForUserId: string | null = null;

  // Signals for reactive updates
  settings = signal<UserSettings | null>(null);
  navigationOptions = signal<Array<{ value: string; label: string }>>([]);
  isLoading = signal<boolean>(false);
  isLoaded = signal<boolean>(false);

  constructor(private apiService: ApiService) {}

  // Observable getters
  get settings$(): Observable<UserSettings | null> {
    return this.settingsSubject.asObservable();
  }

  get navigationOptions$(): Observable<Array<{ value: string; label: string }>> {
    return this.navigationOptionsSubject.asObservable();
  }

  get isLoading$(): Observable<boolean> {
    return this.isLoadingSubject.asObservable();
  }

  get isLoaded$(): Observable<boolean> {
    return this.isLoadedSubject.asObservable();
  }

  /**
   * Load user settings from API
   */
  loadUserSettings(): Observable<UserSettingsResponse> {
    this.isLoading.set(true);
    this.isLoadingSubject.next(true);

    return this.apiService.getSettings();
  }

  /**
   * Process and store user settings data
   */
  processUserSettings(response: UserSettingsResponse): void {
    if (response.success && response.data) {
      const userData = response.data;
      
      // Parse name into firstName and lastName
      const fullName = userData?.userDetails?.name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Update navigation options from navigation data
      this.updateNavigationOptions(userData?.navigationDetails);
      
      const settings: UserSettings = {
        firstName: firstName,
        lastName: lastName,
        email: userData?.userDetails?.email || '',
        title: userData?.userDetails?.jobInfo || '',
        aboutMe: userData?.userDetails?.aboutMe || '',
        phone: userData?.userDetails?.phone || '',
        ext: userData?.userDetails?.ext || '',
        mobile: userData?.userDetails?.mobile || userData?.userDetails?.phone || '',
        address: userData?.userDetails?.address || '',
        city: userData?.userDetails?.city || '',
        state: userData?.userDetails?.state || '',
        postalCode: userData?.userDetails?.postalCode || '',
        country: userData?.userDetails?.country || '',
        defaultPage: userData?.userDetails?.redirectTo || userData?.userDetails?.defaultPage || 'my-work',
        redirectTo: userData?.userDetails?.redirectTo || '',
        emailNotifications: userData?.userDetails?.emailNotifications !== undefined ? userData?.userDetails?.emailNotifications : true,
        emailTime: userData?.userDetails?.emailTime || '07:00',
        isActive: userData?.userDetails?.isActive !== undefined ? userData?.userDetails?.isActive : true,
        accessLevel: userData?.userDetails?.accessLevel || 'client-access',
        profilePhoto: userData?.userDetails?.image || userData?.userDetails?.profilePhoto || '',
        name: fullName,
        job: userData?.userDetails?.jobInfo || '',
        mailTemplates: userData?.mailTemplates || [],
        disabledMailTemplateIDs: userData?.userDetails?.disabledMailTemplateIDs || []
      };

      // Update signals and subjects
      this.settings.set(settings);
      this.settingsSubject.next(settings);
      this.loadedForUserId = localStorage.getItem('userID');
      this.isLoaded.set(true);
      this.isLoadedSubject.next(true);
    }
    
    this.isLoading.set(false);
    this.isLoadingSubject.next(false);
  }

  /**
   * Update navigation options from navigation data
   */
  private updateNavigationOptions(navigationDetails: NavigationDetails[] | undefined): void {
    if (!navigationDetails || navigationDetails.length === 0) {
      console.warn('No navigation details found, using fallback options');
      this.navigationOptions.set([]);
      this.navigationOptionsSubject.next([]);
      return;
    }

    // Find the active navigation details
    const activeNavigation = navigationDetails.find(nav => nav.status === 'active');
    
    if (!activeNavigation || !activeNavigation.navigationData || activeNavigation.navigationData.length === 0) {
      console.warn('No active navigation data found, using fallback options');
      this.navigationOptions.set([]);
      this.navigationOptionsSubject.next([]);
      return;
    }

    // Convert navigation data to dropdown options
    const dynamicOptions = activeNavigation.navigationData.map(navItem => ({
      value: navItem.path,
      label: navItem.name
    }));

    this.navigationOptions.set(dynamicOptions);
    this.navigationOptionsSubject.next(dynamicOptions);
  }

  /**
   * Get current settings
   */
  getCurrentSettings(): UserSettings | null {
    return this.settings();
  }

  /**
   * Get current navigation options
   */
  getCurrentNavigationOptions(): Array<{ value: string; label: string }> {
    return this.navigationOptions();
  }

  /**
   * Check if settings are loaded
   */
  areSettingsLoaded(): boolean {
    const currentUserId = localStorage.getItem('userID');
    return (
      this.isLoaded() &&
      !!currentUserId &&
      this.loadedForUserId === currentUserId
    );
  }

  /**
   * Check if settings are currently loading
   */
  areSettingsLoading(): boolean {
    return this.isLoading();
  }

  /**
   * Clear settings data (useful for logout)
   */
  clearSettings(): void {
    this.settings.set(null);
    this.settingsSubject.next(null);
    this.navigationOptions.set([]);
    this.navigationOptionsSubject.next([]);
    this.loadedForUserId = null;
    this.isLoaded.set(false);
    this.isLoadedSubject.next(false);
    this.isLoading.set(false);
    this.isLoadingSubject.next(false);
  }
}
